import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
// @ts-ignore — AuthContext es .jsx
import { useAuth } from "@/contexts/AuthContext";
import { HomeLayout } from "../components/layout/HomeLayout";
import { BeginnerPathPhaseShell } from "../components/beginnerPath/BeginnerPathPhaseShell";
import { ChordPathLibrary } from "../components/chords/path/ChordPathLibrary";
import { ChordPathMissionStudio } from "../components/chords/path/ChordPathMissionStudio";
import { useBeginnerPathEntry } from "../hooks/useBeginnerPathEntry";
import {
  CHORD_PATH_MISSIONS,
  getChordPathMissionById,
} from "../lib/chords/chordPathPlan";
import {
  loadChordPathProgress,
  markChordPathMissionComplete,
  saveChordPathProgress,
} from "../lib/chords/chordPathProgress";
import {
  isBeginnerPathPreviewUser,
  loadBeginnerPathProgress,
  markBeginnerPhaseComplete,
  saveBeginnerPathProgress,
} from "../lib/beginnerPath/beginnerPathPlan";
import { HOME_THEME as C } from "../lib/homeTheme";
import { BEGINNER_PATH_PAGE_CLASS } from "../lib/beginnerPath/beginnerPathLayout";

export const ChordPathPage: React.FC = () => {
  const navigate = useNavigate();
  const { fromPath, phase } = useBeginnerPathEntry();
  const pathMode = fromPath && phase?.id === "chords-learn";
  const { user } = useAuth() as { user?: { email?: string | null } | null };
  const preview = useMemo(
    () => isBeginnerPathPreviewUser(user?.email),
    [user?.email]
  );

  const [activeMissionId, setActiveMissionId] = useState<string | null>(null);
  const [progress, setProgress] = useState(loadChordPathProgress);

  useEffect(() => {
    saveChordPathProgress(progress);
    const allDone = CHORD_PATH_MISSIONS.every((m) =>
      progress.completedMissionIds.includes(m.id)
    );
    if (allDone) {
      const bp = loadBeginnerPathProgress();
      if (!bp.completedPhaseIds.includes("chords-learn")) {
        saveBeginnerPathProgress(markBeginnerPhaseComplete(bp, "chords-learn"));
      }
    }
  }, [progress]);

  const activeMission = activeMissionId
    ? getChordPathMissionById(activeMissionId)
    : null;

  const handleMissionComplete = useCallback(() => {
    if (!activeMissionId) return;
    const nextProgress = markChordPathMissionComplete(progress, activeMissionId);
    setProgress(nextProgress);
    saveChordPathProgress(nextProgress);

    const allDone = CHORD_PATH_MISSIONS.every((m) =>
      nextProgress.completedMissionIds.includes(m.id)
    );
    if (allDone && pathMode) {
      const bp = loadBeginnerPathProgress();
      saveBeginnerPathProgress(markBeginnerPhaseComplete(bp, "chords-learn"));
      navigate("/learn/path");
      return;
    }
    setActiveMissionId(null);
  }, [activeMissionId, navigate, pathMode, progress]);

  const inner = activeMission ? (
    <ChordPathMissionStudio
      mission={activeMission}
      wasCompletedBefore={progress.completedMissionIds.includes(activeMission.id)}
      pathMode={!!pathMode}
      onBack={() => setActiveMissionId(null)}
      onComplete={handleMissionComplete}
    />
  ) : (
    <ChordPathLibrary
      progress={progress}
      pathMode={!!pathMode}
      preview={preview}
      onSelectMission={setActiveMissionId}
    />
  );

  if (pathMode && phase) {
    return (
      <BeginnerPathPhaseShell phase={phase} fullBleed={!!activeMission}>
        {inner}
      </BeginnerPathPhaseShell>
    );
  }

  return (
    <HomeLayout>
      <div className={BEGINNER_PATH_PAGE_CLASS}>
        <button
          type="button"
          onClick={() => navigate("/learn/path")}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase transition-colors hover:text-[#99f6e4]"
          style={{ color: C.muted }}
        >
          <ArrowLeft className="h-4 w-4" />
          Mapa de caminos
        </button>
        <header className="mt-8 mb-8">
          <p className="text-xs tracking-widest uppercase text-[#99f6e4]/70">
            Camino 2 · Acordes
          </p>
          <h1 className="mt-3 font-serif text-3xl font-light text-white/90">
            Familia Em / E / Am
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-white/55">
            Geometría de los primeros acordes y auditoría de sonido limpio nota a nota.
          </p>
        </header>
        {inner}
      </div>
    </HomeLayout>
  );
};

export default ChordPathPage;
